"use client";

import { usePathname } from "next/navigation";
import type { JSX } from "react";
import { useCallback, useEffect, useRef } from "react";
import { Icon, Image, Link } from "@/components";
import type { AsideProps } from "@/components/types";
import useWindowSize from "@/hooks/useWindowSize";
import { useLayout } from "@/providers";

/**
 * @name SidebarVertical
 * @description Renders a premium vertical sidebar with flat navigation structure.
 */
const SidebarVertical = ({
  data,
  setSidebar,
  sidebarIsOpen
}: AsideProps): JSX.Element => {
  const { t } = useLayout();
  const path = usePathname();
  const { windowSize } = useWindowSize();
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const currentWindowSize = window.innerWidth;
      if (!sidebarIsOpen) return;

      const target = event.target as Element;
      if (target.id === "sidebar-toggle" || target.closest("#sidebar-toggle")) {
        return;
      }

      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        if (currentWindowSize < 1400) {
          setSidebar(false);
        }
      }
    },
    [setSidebar, sidebarIsOpen]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <aside
      ref={sidebarRef}
      className={`w-[280px] xxxl:w-[320px] bg-sidebar border-r border-sidebar-border z-[105] ${
        sidebarIsOpen
          ? "translate-x-0 visible shadow-xl"
          : "ltr:-translate-x-full rtl:translate-x-full invisible shadow-none"
      } sidebar fixed ltr:left-0 rtl:right-0 h-[calc(100%-84px)] top-[84px] transition-all duration-300 ease-in-out flex flex-col`}
    >
      {/* Menu Section - Flex Grow */}
      <div className="  flex-1 overflow-y-auto py-2 scrollbar-thin scrollbar-thumb-sidebar-border">
        <div className="flex flex-col gap-1">
          {data?.map((item: AsideProps["data"][number]) => {
            const isActive = path === String(item.value);
            // Check if icon is an image path (starts with /) or a font class
            const isImageIcon = String(item.icon).startsWith("/");

            return (
              <Link
                replace
                key={item.id}
                href={String(item.value)}
                target={item.target ?? "_self"}
                onClick={() => {
                  if (windowSize && windowSize < 1400) setSidebar(false);
                }}
                className={`
                  group flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                  ${
                    isActive
                      ? "bg-sidebar-accent text-sidebar-accent-foreground font-bold shadow-sm"
                      : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground font-medium"
                  }
                `}
              >
                <div
                  className={`
                  w-6 h-6 flex justify-center items-center transition-all duration-200
                   ${!isImageIcon && "text-xl"}
                `}
                >
                  {isImageIcon ? (
                    <Image
                      src={String(item.icon)}
                      alt={item.label || "icon"}
                      width={24}
                      height={24}
                      className={`object-contain transition-all duration-200 dark:brightness-0 dark:invert ${isActive ? "opacity-100" : "opacity-70 group-hover:opacity-100"}`}
                    />
                  ) : (
                    <Icon name={String(item.icon)} size="2x" />
                  )}
                </div>
                <span className="text-[15px] leading-none pt-0.5">
                  {t(item.label || "")}
                </span>

                {/* Active Indicator (Dot) */}
                {isActive && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-sidebar-primary shadow-sm" />
                )}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Logout Section - Fixed Bottom relative to flex */}
      <div className="p-4 border-t border-sidebar-border shrink-0 bg-sidebar">
        <Link
          replace
          href="/signOut"
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sidebar-foreground/70 hover:bg-destructive/10 hover:text-destructive transition-all duration-200 group font-medium"
        >
          <div className="w-6 h-6 flex justify-center items-center">
            {/* Using generic generic logout icon or specific one if available */}
            <Icon
              name="fa-solid fa-arrow-right-from-bracket"
              size="2x"
              className="fa-solid fa-arrow-right-from-bracket text-lg opacity-70 group-hover:opacity-100"
            />
          </div>
          <span className="text-[15px] leading-none pt-0.5">
            {t("signOut")}
          </span>
        </Link>
      </div>
    </aside>
  );
};

export default SidebarVertical;
